import { AmenitiesList } from '@/components/amenities';
import { amenityCrudConfig, type Amenity } from '@/config/objects/amenities';
import { amenityTypeCrudConfig, type AmenityType } from '@/config/objects/amenity-types';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Amenity Type detail page
 */
export function AmenityTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [amenityType, setAmenityType] = React.useState<AmenityType | null>(null);

  /**
   * Delete amenity type mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (amenityTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/amenity-types/${amenityTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate amenity types list to refresh data
        queryClient.invalidateQueries({ queryKey: ['AmenityType'] });
        navigate('/amenity-types');
      } else {
        throw new Error(response.error || 'Failed to delete amenity type');
      }
    },
  });

  // Create custom config for amenity type-specific amenities
  const amenityTypeAmenitiesConfig: CrudConfig<Amenity> = useMemo(
    () => ({
      ...amenityCrudConfig,
      entityName: 'Amenity',
      entityNamePlural: 'Amenities',
      endpoints: {
        ...amenityCrudConfig.endpoints,
        list: `/amenity-types/${id}/amenities`,
        delete: (amenityId: string) => `/amenity-types/${id}/amenities/${amenityId}`,
      },
      listView: {
        ...amenityCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/amenities',
            where: amenityType
              ? {
                  amenityTypeId: {
                    not: amenityType.id,
                  },
                }
              : undefined,
            onAdd: async (amenity: Amenity) => {
              await apiClient.post(`/amenity-types/${id}/amenities`, { amenityId: amenity.id });
              toast.success('Amenity added to amenity type');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (amenity: Amenity) => {
              await apiClient.delete(`/amenity-types/${id}/amenities/${amenity.id}`);
              toast.success('Amenity removed from amenity type');
            },
          },
        },
      },
    }),
    [amenityType, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Amenity Type Not Found</h2>
          <p className="text-muted-foreground">The requested amenity type could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this amenity type
   */
  const handleEdit = (amenityType: AmenityType) => {
    navigate(`/amenity-types/${amenityType.id}/edit`);
  };

  /**
   * Handle amenity type deletion
   */
  const handleDelete = async (amenityType: AmenityType) => {
    return toast.promise(deleteMutation.mutateAsync(amenityType.id), {
      loading: 'Deleting amenity type...',
      success: 'Amenity type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete amenity type',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Amenity Types', href: '/amenity-types' },
    { label: amenityType?.name || 'Amenity Type', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={amenityTypeCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(amenityTypeData: AmenityType) => setAmenityType(amenityTypeData)}
        />

        {/* Amenities Section */}
        <AmenitiesList config={amenityTypeAmenitiesConfig} className="p-0" baseUrl="/amenities" />
      </div>
    </PageContainer>
  );
}
